(function () {
  const STORAGE_KEY = 'nmkj-wishlist';

  // The piece snapshot the "tell me when one arrives" form sends to /watch: what the
  // card knew at save time, refreshed with the product JSON the saved-pieces page
  // already fetches (variants[0].sku, vendor). Keyed by product id.
  const pieceDetails = new Map();

  function getWishlist() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  function setWishlist(items) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    document.dispatchEvent(new CustomEvent('nmkj:wishlist-updated', { detail: { items } }));
  }

  function formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function stripHtml(html) {
    if (!html) return '';
    const node = document.createElement('div');
    node.innerHTML = html;
    return (node.textContent || node.innerText || '').replace(/\s+/g, ' ').trim();
  }

  function truncateText(text, length) {
    if (!text || text.length <= length) return text || '';
    return text.slice(0, length).trim() + '…';
  }

  function toggleWishlist(payload) {
    const id = String(payload.id);
    const items = getWishlist();
    const index = items.findIndex((item) => String(item.id) === id);

    if (index >= 0) {
      items.splice(index, 1);
      setWishlist(items);
      return false;
    }

    items.unshift({
      id,
      handle: payload.handle || '',
      title: payload.title || '',
      description: payload.description || '',
      price: payload.price || '',
      image: payload.image || '',
      url: payload.url || '',
      sku: payload.sku || '',
      vendor: payload.vendor || '',
      addedAt: Date.now(),
    });
    setWishlist(items);
    return true;
  }

  // The header "Saved" count. Hidden at zero so an empty list shows a plain heart.
  function syncCount() {
    const count = getWishlist().length;
    document.querySelectorAll('[data-nmkj-wishlist-count]').forEach((badge) => {
      badge.textContent = count > 99 ? '99+' : String(count);
      badge.hidden = count === 0;
    });
    document.querySelectorAll('[data-nmkj-wishlist-link]').forEach((link) => {
      link.setAttribute('aria-label', count ? `Saved pieces (${count})` : 'Saved pieces');
    });
  }

  function syncButtons(root) {
    const scope = root && root.querySelectorAll ? root : document;
    const ids = new Set(getWishlist().map((item) => String(item.id)));

    scope.querySelectorAll('[data-nmkj-wishlist-toggle]').forEach((button) => {
      const active = ids.has(String(button.dataset.productId));
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      button.setAttribute('aria-label', active ? 'Remove from saved pieces' : 'Save this piece');
    });
  }

  /** The saved snapshot as /watch wants it: [{sku, handle, title, price, vendor}]. */
  function getPieces() {
    return getWishlist().map((item) => {
      const detail = pieceDetails.get(String(item.id)) || {};
      return {
        sku: detail.sku || item.sku || '',
        handle: item.handle || detail.handle || '',
        title: detail.title || item.title || '',
        price: detail.price || item.price || '',
        vendor: detail.vendor || item.vendor || '',
      };
    });
  }

  // gone: 'sold' (the PDP answered available:false) or 'missing' (no PDP any more — every
  // piece is one-of-one and the connector unpublishes on sale, so a 404 is a sold or
  // withdrawn piece). A gone card keeps its place in the grid — it still feeds the
  // "tell me when a piece like these arrives" line — but says so, and points at the
  // sourcing form instead of a dead PDP.
  function buildCardMarkup(product, cached, gone) {
    const variant = product.variants && product.variants[0] ? product.variants[0] : null;
    const image = product.featured_image || product.images?.[0] || cached?.image || '';
    const sourcingForm = gone ? document.querySelector('[data-nmkj-sourcing-form]') : null;
    const url = gone
      ? (sourcingForm && sourcingForm.id ? `#${sourcingForm.id}` : '#')
      : product.url || cached?.url || (product.handle ? `/products/${product.handle}` : '#');
    const title = product.title || cached?.title || '';
    const description = truncateText(
      stripHtml(product.description || product.body_html || cached?.description || ''),
      120
    );
    // The card's own price first: Liquid formatted it in the shopper's currency when
    // they saved it. Shopify.formatMoney is not defined on this storefront, so the
    // fallback would print raw minor units as dollars ("$423000.00" for ¥423,000).
    const price = cached?.price || (variant ? formatMoney(variant.price) : '');
    const productId = product.id || cached?.id || '';
    const sku = (variant && variant.sku) || cached?.sku || '';
    const vendor = product.vendor || cached?.vendor || '';

    if (productId) {
      pieceDetails.set(String(productId), { sku, vendor, title, price, handle: product.handle || cached?.handle || '' });
    }

    const goneLabel = gone === 'sold' ? 'Sold' : gone ? 'No longer available' : '';
    const priceLine = gone
      ? `<p class="nmkj-rec-card__price nmkj-rec-card__gone"><span>${goneLabel}</span> · <a href="${url}">Ask us to source one</a></p>`
      : `<p class="nmkj-rec-card__price">${price}</p>`;

    return `
      <article class="nmkj-rec-card${gone ? ' nmkj-rec-card--gone' : ''}">
        <div class="nmkj-rec-card__media-wrap">
          <a href="${url}" class="nmkj-rec-card__media-link" aria-label="${title.replace(/"/g, '&quot;')}${gone ? ` (${goneLabel.toLowerCase()})` : ''}">
            <div class="nmkj-rec-card__media">
              ${
                image
                  ? `<img src="${image}" alt="${title.replace(/"/g, '&quot;')}" class="nmkj-rec-card__image" loading="lazy" width="600" height="600">`
                  : `<svg class="nmkj-rec-card__placeholder" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 525.5 525.5"><path d="M375.5 345.2c0-.1 0-.1 0 0 0-.1 0-.1 0 0z"/></svg>`
              }
            </div>
          </a>
          <button
            type="button"
            class="nmkj-rec-card__wishlist"
            data-nmkj-wishlist-toggle
            data-product-id="${productId}"
            data-product-handle="${product.handle || cached?.handle || ''}"
            data-product-title="${title.replace(/"/g, '&quot;')}"
            data-product-description="${description.replace(/"/g, '&quot;')}"
            data-product-price="${price.replace(/"/g, '&quot;')}"
            data-product-image="${image}"
            data-product-url="${url}"
            data-product-sku="${String(sku).replace(/"/g, '&quot;')}"
            data-product-vendor="${String(vendor).replace(/"/g, '&quot;')}"
            aria-label="Save this piece"
          >
            <svg width="20" height="18" viewBox="0 0 22 20" fill="none" aria-hidden="true">
              <path d="M11 18.35L9.55 17.03C4.4 12.36 1 9.28 1 5.5C1 2.42 3.42 0 6.5 0C8.24 0 9.91 0.81 11 2.09C12.09 0.81 13.76 0 15.5 0C18.58 0 21 2.42 21 5.5C21 9.28 17.6 12.36 12.45 17.04L11 18.35Z" stroke="currentColor" stroke-width="1.2" fill="none"/>
            </svg>
          </button>
        </div>
        <div class="nmkj-rec-card__info">
          <a href="${url}" class="nmkj-rec-card__title">${title}</a>
          ${description ? `<p class="nmkj-rec-card__description">${description}</p>` : ''}
          ${priceLine}
        </div>
      </article>
    `;
  }

  async function renderWishlistPage() {
    const grid = document.getElementById('nmkj-wishlist-products');
    const empty = document.getElementById('nmkj-wishlist-empty');
    if (!grid) return;

    const items = getWishlist();
    if (!items.length) {
      grid.innerHTML = '';
      if (empty) empty.hidden = false;
      document.dispatchEvent(new CustomEvent('nmkj:wishlist-rendered', { detail: { pieces: [] } }));
      return;
    }

    if (empty) empty.hidden = true;

    const cards = await Promise.all(
      items.map(async (item) => {
        if (!item.handle) return buildCardMarkup({}, item);

        try {
          const response = await fetch(`/products/${item.handle}.js`);
          if (response.status === 404) return buildCardMarkup({}, item, 'missing');
          if (!response.ok) throw new Error('Product not found');
          const product = await response.json();
          return buildCardMarkup(product, item, product.available === false ? 'sold' : null);
        } catch {
          // Network trouble, not a verdict on the piece: render the cached snapshot as saved.
          return buildCardMarkup({}, item);
        }
      })
    );

    grid.innerHTML = cards
      .map((card) => `<div class="resource-list__item nmkj-wishlist-grid__item">${card}</div>`)
      .join('');

    syncButtons(grid);
    document.dispatchEvent(new CustomEvent('nmkj:wishlist-rendered', { detail: { pieces: getPieces() } }));
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-nmkj-wishlist-toggle]');
    if (!button) return;

    event.preventDefault();
    event.stopPropagation();

    toggleWishlist({
      id: button.dataset.productId,
      handle: button.dataset.productHandle,
      title: button.dataset.productTitle,
      description: button.dataset.productDescription,
      price: button.dataset.productPrice,
      image: button.dataset.productImage,
      url: button.dataset.productUrl,
      sku: button.dataset.productSku,
      vendor: button.dataset.productVendor,
    });

    syncButtons(button.closest('product-recommendations') || button.closest('#nmkj-wishlist-products') || document);

    if (document.getElementById('nmkj-wishlist-products')) {
      renderWishlistPage();
    }
  });

  document.addEventListener('nmkj:wishlist-updated', () => {
    syncButtons();
    syncCount();
  });
  document.addEventListener('nmkj:content-updated', (event) => {
    syncButtons(event.detail?.target || document);
  });
  document.addEventListener('DOMContentLoaded', () => {
    syncButtons();
    syncCount();
    renderWishlistPage();
  });

  if (document.readyState !== 'loading') {
    syncButtons();
    syncCount();
    renderWishlistPage();
  }

  window.nmkjWishlist = { getItems: getWishlist, getPieces };
})();
